import { useState, useRef, useEffect } from 'react';
import {
  FormGroup,
  Label,
  FormText,
  Button,
  Progress,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { getInvoiceSignedUrl, uploadInvoice } from '../Helpers/Api';
import { Previewer } from '../../../../../components/DocumentPreviewer/Previewer';

const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

const setNestedValue = (obj, path, value) => {
  const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
  const keys = normalizedPath.split('.');
  const newObj = deepClone(obj);

  let current = newObj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key]) {
      current[key] = isNaN(keys[i + 1]) ? {} : [];
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
  return newObj;
};

const FileUploader = ({
  formik,
  fieldName,
  label = 'Upload File',
  isDisabled = false,
  onSuccess = () => { },
  acceptedFileTypes = '*',
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [localFileUrl, setLocalFileUrl] = useState('');
  const fileInputRef = useRef(null);

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
    const keys = normalizedPath.split('.');
    return keys.reduce((acc, key) => {
      return acc !== undefined ? acc[key] : undefined;
    }, obj);
  };

  const formikValue = getNestedValue(formik.values, fieldName) || '';
  const currentValue = localFileUrl || formikValue;

  useEffect(() => {
    if (formikValue && !localFileUrl) {
      setLocalFileUrl(formikValue);
    }
  }, [formikValue, fieldName]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);

      try {
        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('invoice', selectedFile);

        const propertyId = formik.values.property_id;

        const response = await uploadInvoice(propertyId, formData, (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(progress);
          }
        });

        setUploadProgress(100);

        const fileUrl = response.fileUrl;

        setLocalFileUrl(fileUrl);

        formik.setFieldValue(fieldName, fileUrl);
        onSuccess(response);

        setFile(null);
        setError(null);
        formik.setFieldTouched(fieldName, true);
      } catch (err) {
        console.error('Error during upload:', err);
        setError('Failed to upload file. Please try again.');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setLocalFileUrl('');
    const updatedValues = setNestedValue(formik.values, fieldName, '');
    formik.setValues(updatedValues);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePreview = async () => {
    if (!currentValue) return;

    try {
      setLoadingPreview(true);
      setError(null);
      const { signed_url } = await getInvoiceSignedUrl(currentValue);
      setPreviewUrl(signed_url);
      setPreviewOpen(true);
    } catch (err) {
      console.error('Error getting preview:', err);
      setError('Failed to load file preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl(null);
  };

  const getFileName = (url) => {
    if (!url) return '';
    return url.split('/').pop();
  };

  return (
    <FormGroup>
      <Label for={fieldName} style={{ fontSize: '0.875rem' }}>
        {label}
      </Label>

      {error && (
        <Alert color="danger" style={{ fontSize: '0.75rem', padding: '0.5rem', marginBottom: '0.5rem' }}>
          {error}
        </Alert>
      )}

      <div className="d-flex align-items-center mb-2">
        <input
          type="file"
          id={`${fieldName.replace(/\[|\]|\./g, '-')}-file`}
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept={acceptedFileTypes}
          disabled={isDisabled || uploading}
        />

        <Button
          color="secondary"
          size="sm"
          outline
          onClick={() => fileInputRef.current.click()}
          disabled={isDisabled || uploading}
          style={{ marginRight: '0.5rem', fontSize: '0.75rem' }}
        >
          Browse Files
        </Button>

        {file && (
          <span style={{ fontSize: '0.75rem', marginRight: '0.5rem' }}>
            {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
          </span>
        )}
      </div>

      {uploading && (
        <Progress value={uploadProgress} style={{ height: '6px', marginBottom: '0.5rem' }}>
          {uploadProgress}%
        </Progress>
      )}

      {currentValue && (
        <div
          style={{
            fontSize: '0.75rem',
            backgroundColor: '#f8f9fa',
            padding: '0.5rem',
            borderRadius: '0.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
          }}
        >
          <span style={{ wordBreak: 'break-all' }}>Current File: {getFileName(currentValue)}</span>
          <div>
            <Button
              color="info"
              size="sm"
              outline
              onClick={handlePreview}
              disabled={isDisabled || loadingPreview || !currentValue}
              style={{ marginRight: '0.5rem', fontSize: '0.75rem' }}
            >
              {loadingPreview ? 'Loading...' : 'Preview'}
            </Button>
            <Button
              color="danger"
              size="sm"
              outline
              onClick={handleClearFile}
              disabled={isDisabled || uploading}
              style={{ fontSize: '0.75rem' }}
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {getNestedValue(formik.touched, fieldName) && getNestedValue(formik.errors, fieldName) && (
        <FormText color="danger">{getNestedValue(formik.errors, fieldName)}</FormText>
      )}

      <Modal isOpen={previewOpen} toggle={handleClosePreview} size="lg">
        <ModalHeader toggle={handleClosePreview}>File Preview: {getFileName(currentValue)}</ModalHeader>
        <ModalBody style={{ padding: 0 }}>
          {previewUrl && (
            <div style={{ overflow: 'auto', maxHeight: '80vh' }}>
              <Previewer signedUrl={previewUrl} url={currentValue} height="80vh" />
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={handleClosePreview}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </FormGroup>
  );
};

export { FileUploader };